/**
 * Typed client for the public-workspace endpoints used by the
 * `PublicWorkspaceSplash` component. The metadata GET is unauthenticated
 * (called before sign-in completes for some flows), the two POSTs require
 * a WorkOS token.
 *
 * Auth + base URL handling is delegated to:
 *   - `useApiClient()` for the authenticated calls (handles auth header,
 *     401-bus notification, NetworkError mapping).
 *   - The shared `fetchPublicWorkspaceMetadata` helper for the GET, since
 *     it can be called from outside the React tree (e.g. before the
 *     ZeroProvider mounts) and doesn't need auth.
 */
import {
  autoJoinResponseSchema,
  communityPreviewSchema,
  fetchPublicWorkspaceMetadata,
  publicWorkspaceAutoJoinPath,
  publicWorkspacePreviewPath,
  publicWorkspaceRequestJoinPath,
  requestJoinResponseSchema,
  type AutoJoinResponse,
  type CommunityPreview,
  type PublicWorkspaceMetadata,
  type RequestJoinBody,
  type RequestJoinResponse,
} from "@ano/shared/api/public-workspaces";
import type { ZodType } from "zod";
import { API_URL } from "@/lib/config";
import { ApiError, type ApiClient } from "./_client";

export interface PublicWorkspacesApi {
  lookupBySlug: (slug: string) => Promise<PublicWorkspaceMetadata | null>;
  preview: (slug: string) => Promise<CommunityPreview | null>;
  autoJoin: (slug: string) => Promise<AutoJoinResponse>;
  requestJoin: (
    slug: string,
    body: RequestJoinBody,
  ) => Promise<RequestJoinResponse>;
}

export function createPublicWorkspacesApi(
  apiClient: ApiClient,
): PublicWorkspacesApi {
  return {
    lookupBySlug: (slug) =>
      fetchPublicWorkspaceMetadata({ apiBaseUrl: API_URL, slug }),
    preview: async (slug) => {
      try {
        const payload = await apiClient.get<unknown>(
          publicWorkspacePreviewPath(slug),
        );
        return parsePublicResponse(
          communityPreviewSchema,
          payload,
          "Invalid public preview response",
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    autoJoin: async (slug) => {
      const payload = await apiClient.post<unknown>(
        publicWorkspaceAutoJoinPath(slug),
      );
      return parsePublicResponse(
        autoJoinResponseSchema,
        payload,
        "Invalid auto-join response",
      );
    },
    requestJoin: async (slug, body) => {
      const payload = await apiClient.post<unknown>(
        publicWorkspaceRequestJoinPath(slug),
        body,
      );
      return parsePublicResponse(
        requestJoinResponseSchema,
        payload,
        "Invalid request-join response",
      );
    },
  };
}

function parsePublicResponse<T>(
  schema: ZodType<T>,
  payload: unknown,
  message: string,
): T {
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(message);
  }
  return parsed.data;
}
